import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { useSystemStore } from '@/store/systemStore';

const DISMISS_KEY = 'env_banner_dismissed';

// Barvna shema usklajena z EnvironmentBadge (testing = oranžna, development = modra).
const ENV_STYLES = {
  testing: 'bg-amber-500 text-white',
  development: 'bg-primary text-primary-foreground',
  production: null,
} as const;

/**
 * T911: opozorilni banner za ne-produkcijska okolja.
 *
 * Prikaže se le, ko je `SystemSettings.environment_type` `testing` ali
 * `development` (vir: javni `GET /api/v1/config/` prek `useSystemStore`).
 * Na produkciji se sam skrije in CSS spremenljivka `--env-banner-h` ostane `0px`,
 * tako da fiksne navigacije ostanejo na `top-0` brez vpliva.
 *
 * Zapre se za sejo (`sessionStorage`) — ob novi seji se spet pokaže, kar ohranja
 * pravno/poslovno vrednost opozorila (cene niso zavezujoče), a ne moti testerja.
 */
export default function TestEnvironmentBanner() {
  const { t } = useTranslation();
  const { settings } = useSystemStore();
  const env = settings?.environment_type ?? 'production';
  const ref = useRef<HTMLDivElement>(null);

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const style = ENV_STYLES[env];
  const visible = Boolean(style) && !dismissed;

  // Zamakni fiksne navigacije za dejansko višino bannerja (robustno ob prelomu besedila).
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (visible && ref.current) {
      root.style.setProperty('--env-banner-h', `${ref.current.offsetHeight}px`);
    } else {
      root.style.setProperty('--env-banner-h', '0px');
    }
    return () => {
      root.style.setProperty('--env-banner-h', '0px');
    };
  }, [visible, env, t]);

  // Posodobi višino tudi ob spremembi velikosti okna (mobilni prelom besedila).
  useEffect(() => {
    if (!visible) return;
    const update = () => {
      if (ref.current) {
        document.documentElement.style.setProperty('--env-banner-h', `${ref.current.offsetHeight}px`);
      }
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [visible]);

  if (!visible || env === 'production') return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      /* sessionStorage ni na voljo — banner se skrije le za trenutni pogled */
    }
    setDismissed(true);
  };

  return (
    <div
      ref={ref}
      role="alert"
      className={`fixed top-0 inset-x-0 z-[200] ${style}`}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-3 py-1.5 sm:px-6">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <p className="flex-1 text-center text-dense font-semibold leading-snug sm:text-body">
          {t(`env_banner.${env}`)}
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('env_banner.dismiss')}
          className="flex-shrink-0 rounded p-1 transition-colors hover:bg-white/20"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
